import { IconProps } from 'almanac-ui/icons/Types'
import { FlaggedFeature } from 'hooks/useFlipperFlag'
import { FunctionComponent, ReactNode, SVGProps, KeyboardEvent } from 'react'
import { FieldsetCompletion, KnowledgeBaseSnippetKind } from 'types'
import * as yup from 'yup'
import { Tone } from 'components/VoiceAndTonesSelect/Types'
import { FormikErrors } from 'formik'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { StepSchema, SupportedMediaType, WizardType } from 'components/Wizards/schemas/Types'
import { InputVariant } from 'almanac-ui/TextInputField/Types'

export interface FieldSchema extends Omit<yup.ObjectSchema<object>, 'describe'> {
  describe: () => {
    tests: Array<any>
    type: string
    oneOf: Array<any>
    optional: boolean
    nullable: boolean
    meta: {
      label: string
      type?: string
      mobilePlaceholder?: string
      placeholder: string
      description?: string
      collapsible?: boolean
      overflow?: boolean
      rows?: number
      hide?: boolean
      autoFocus?: boolean
      snippets?: boolean | Array<KnowledgeBaseSnippetKind>
      featureFlags?: Record<FlaggedFeature, boolean>
      copyToClipboard?: boolean
      autoFill?: boolean
      generate?: boolean
      regenerate?: boolean
      writeMyOwn?: boolean
      generateLabel?: string
      integrationType?: ExternalIntegrationEnum
      supportedProviders?: ExternalIntegrationEnum[]
      typeahead?: boolean
      skipReset?: boolean
      generationType?: WizardType
      isDesign?: boolean
      oneOfPills?: boolean
      pillsCarousel?: boolean
      hideManualSectionPreview?: boolean
      withBrandKit?: boolean
      size?: 'sm' | 'md' | 'lg'
      skipBackgroundRemoval?: boolean
      supportedMediaTypes?: SupportedMediaType[]
      multiSelect?: boolean
    }
  }
}

export interface FieldProps {
  name: string
  schema: FieldSchema
  autoFocus?: boolean
  rightContent?: ReactNode
  labelType?: 'heading' | 'text'
}

export interface FeatureFlaggedFieldProps extends FieldProps {
  featureFlags: Record<FlaggedFeature, boolean>
}

export interface FormSchema<Type = WizardType> extends Omit<yup.ObjectSchema<object>, 'describe' | 'omit'> {
  omit(keys: readonly string[]): FormSchema<Type>
  describe: () => {
    tests: Array<any>
    type: string
    fields: yup.ObjectSchema<object>['fields']
    meta: {
      autofillAutomatically?: boolean
      type: Type
      title: string
      steps: Array<string>
      submitLabel?: string
      promptLibrary?: string
      previewUrl: string
      hide?: boolean
      icon:
        | FunctionComponent<React.PropsWithChildren<SVGProps<SVGSVGElement> & IconProps>>
        | React.ForwardRefExoticComponent<React.RefAttributes<SVGSVGElement>>
    }
  }
}

export interface FormProps {
  schema: FormSchema | StepSchema
  initialValues: any
  initialFormStatus?: FormStatus
  onSubmit: (values: any) => void
}

export interface VoiceInputProps {
  onVoiceChange: (voiceId: number | undefined) => any
  onTonesChange: (tones: Tone[]) => any
  tones: Tone[]
  voiceId?: number
  popoverClassName?: string
}

export interface FormikVoiceInputProps {
  voiceFieldName?: string
  tonesFieldName?: string
}

export interface FillFieldsButtonProps {
  fields: Array<string>
  generationType: string
  onCreated?: (fieldsetCompletion: FieldsetCompletion) => void
}

export interface AutoCompleteFieldsProps {
  fieldsetCompletion: FieldsetCompletion
}

export interface SectionsProps {
  schema: FormSchema
}

export interface NextButtonProps extends Pick<ButtonProps, 'variant' | 'isDisabled'> {
  fields: Array<[string, any]>
  onNext: (errors: FormikErrors<any>) => void
}

export interface ManualFieldsSectionProps {
  header?: ReactNode
  isComplete: boolean
  fields: Array<[string, any]>
  onAutofill: (errors: FormikErrors<any>) => void
  onNext: (errors: FormikErrors<any>) => void
  onEdit: () => void
}

export enum FormStatus {
  ManuallyFilled = 'manuallyFilled',
  AutoFilling = 'autoFilling',
  AutoCompleted = 'autoCompleted',
  AutoFilled = 'autoFilled',
}

export interface SnippetTypeaheadInputProps extends FieldProps {
  actionOnPress?: () => void
  actionDisabled?: boolean
  useMoreContext?: boolean
  actions?: ReactNode
  variant?: InputVariant
  isLoading?: boolean
  typeaheadDisabled?: boolean
  setWritingOwn?: (value: boolean) => void
  onKeyDown?: (event: KeyboardEvent) => void
}
